/**
 * EditFormPage.tsx
 * This page fetches a single pitch submission by its ID from the URL, pre-populates the edit form,
 * and handles updates using pitchesApi.update with Formik integration.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, FormikHelpers } from "formik";
import ReleaseInfo from "./components/ReleaseInfo/ReleaseInfo";
import PlatformSelection from "./components/PlatformSelector/PlatformSelection";
import GeneralReleaseInfo from "./components/GeneralReleaseInfo/GeneralReleaseInfo";
import DspSpecificInfo from "./components/DSPSpecificInfo/DspSpecificInfo";
import { SelectedDSPs } from "../PitchFormPage/types/form";
import { createPitchValidationSchema } from "./components/Schema/pitchValidationSchema";

import { pitchesApi } from "../repositories/pitches";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import Swal from "sweetalert2";
import { Value } from "@radix-ui/react-select";
import { YouTubePromotionSurface } from './types/dropdownTypes';

const EditFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDrafting, setIsDrafting] = useState<boolean>(false);
  const [showArtistUpdatePrompt, setShowArtistUpdatePrompt] = useState<boolean>(false);
  const [artistUpdates, setArtistUpdates] = useState<any>(null);
  const [currentPitchData, setCurrentPitchData] = useState<any>(null);
  // Remove showErrorSummary state since we'll use toast instead


  // Initialize empty form data
  const [initialFormValues, setInitialFormValues] = useState<any>({
    // Basic Artist and Release Information
    artistName: "",
    releaseTitle: "",
    releaseDate: "",
    configuration: "",

    // Identifiers
    releaseUpc: "",
    focusTrackName: "",
    trackIsrc: "",

    // Artist Details
    featuredArtists: "",
    featuredArtistUri: "",
    artistHometown: "",
    artistCurrentTown: "",

    // Release Descriptions
    altTitles: "",
    shortDescription: "",
    longDescription: "",
    lyrics: "",
    songDescription: [],

    // Musical Characteristics
    mainGenre: [],
    songMood: [],
    language: [],

    // Links and Media
    spotifyUri: "",
    linkToListen: "",
    promoPhotos: "",
    images:[],
    socialsLinks: "",

    // Platform-Specific Details
    similarArtists: "",
    asin: "",
    TIDALpage: "",
    soundCloudProfileUrl: "",
    soundCloudContact: "",
    distributor: "",
    appleId: "",
    aplleTrackId: "",

    // Platform-Specific Flags and Priorities
    pinToThisIsPlaylist: false,
    addAmazonVoiceIntro: false,
    pitchToAudiomackTrending: false,
    isPremierProgram: false,

    // Subgenres
    spotifySubgenre: [],
    amazonSubgenre: [],
    deezerSubgenre: [],
    audiomackSubgenre: [],
    TIDALSubgenre: [],
    soundCloudSubgenre: [],

    // Spotify specific
    musicCulture: [],
    instruments: [],
    songStyle: [],

    // Amazon specific
    listenersLocation: [],
    songCategory: [],

    // Deezer specific
    priority: "",
    deezerEditorialTeam: "",

    // TIDAL specific
    artistCountry: "",
    LabelDistributor: "",

    // SoundCloud specific
    promotionTerritories: [],
    freshMusicFriday: [],
    playlistConsideration: "",
    soundCloudReleaseStage: "",

    // Status
    status: "",

    // YouTube specific
    youtubePromotionSurface: [],
    // youtubeSongDetails: [],
    youtubeReleaseStage: "",
    youtubePromotionDate: "",
    youtubeTerritories: [],
  });

  const [selectedDSPs, setSelectedDSPs] = useState<SelectedDSPs>({
    spotify: false,
    amazon: false,
    deezer: false,
    TIDAL: false,
    audiomack: false,
    soundcloud: false,
    apple: false,
    youtube: false,
    email: false,
  });

  // Helper function to convert string to array
  const stringToArray = (value: string | null | undefined): string[] => {
    if (!value) return [];
    return value.split(',').map(item => item.trim()).filter(item => item.length > 0);
  };

  // Check for artist updates
  const checkForArtistUpdates = async (pitchData: any) => {
    if (!pitchData.artistName || pitchData.status !== 'draft') {
      return;
    }

    try {
      
      const response = await pitchesApi.checkArtistUpdates(Number(id), pitchData.artistName);
      if (response.data.data.hasUpdates) {
        setArtistUpdates(response.data);
        setShowArtistUpdatePrompt(true);
      }
    } catch (error) {
      console.error("❌ Error checking artist updates:", error);
    }
  };

  // Apply artist updates to form
  const applyArtistUpdates = async () => {
    try {
      setLoading(true);
      
      const response = await pitchesApi.syncArtistData(Number(id), currentPitchData.artistName);
      if (response.data.success) {
        await fetchSubmission();
        
        Swal.fire({
          title: 'Artist Data Updated!',
          text: 'Your draft has been updated with the latest artist information.',
          icon: 'success',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error("❌ Error syncing artist data:", error);
      Swal.fire({
        title: 'Update Failed',
        text: 'Failed to update artist data. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setLoading(false);
      setShowArtistUpdatePrompt(false);
    }
  };

  // Fetch submission data when the component mounts
  const fetchSubmission = async () => {
    try {
      const response = await pitchesApi.getOne(Number(id));
      const { data } = response.data;
      
      setCurrentPitchData(data);
      const formValues: any = {
        // Basic Information
        artistName: data.artistName || "",
        releaseTitle: data.releaseTitle || "",
        releaseDate: data.releaseDate || "",
        configuration: data.configuration || "",
        releaseUpc: data.releaseUPC || "",
        focusTrackName: data.focusTrackName || "",
        trackIsrc: data.trackISRC || "",
        featuredArtists: data.featuredArtists || "",
        featuredArtistUri: data.featuredArtistURI || "",
        altTitles: data.altTitles || "",
        shortDescription: data.shortDescription || "",
        longDescription: data.longDescription || "",
        lyrics: data.lyrics || "",
        songDescription: stringToArray(data.songDescription),
        mainGenre: stringToArray(data.mainGenre),
        songMood: stringToArray(data.songMood),
        language: stringToArray(data.language),
        linkToListen: data.linkToListen || "",
        promoPhotos: data.promoPhotos || "",
        images: Array.isArray(data.images) ? data.images : [],
        status: data.status || "",

        // Spotify Info
        spotifyUri: data.SpotifyInfo?.spotifyUri || "",
        artistHometown: data.SpotifyInfo?.artistHometown || "",
        artistCurrentTown: data.SpotifyInfo?.artistCurrentTown || "",
        pinToThisIsPlaylist: data.SpotifyInfo?.pinToPlaylist || false,
        spotifySubgenre: stringToArray(data.SpotifyInfo?.subgenre),
        musicCulture: stringToArray(data.SpotifyInfo?.musicCulture),
        instruments: stringToArray(data.SpotifyInfo?.instruments),
        songStyle: stringToArray(data.SpotifyInfo?.songStyle),

        // Amazon Info
        addAmazonVoiceIntro: data.AmazonInfo?.addAmazonVoiceIntro || false,
        listenersLocation: stringToArray(data.AmazonInfo?.listenersLocation),
        similarArtists: data.AmazonInfo?.similarArtists || "",
        songCategory: stringToArray(data.AmazonInfo?.songCategory),
        amazonSubgenre: stringToArray(data.AmazonInfo?.subgenre),
        asin: data.AmazonInfo?.asin || "",

        // Deezer Info
        priority: data.DeezerInfo?.priority || "",
        deezerEditorialTeam: data.DeezerInfo?.editorialTeam || "",
        deezerSubgenre: stringToArray(data.DeezerInfo?.subgenre),

        // TIDAL Info
        TIDALSubgenre: stringToArray(data.TidalInfo?.subgenre),
        TIDALpage: data.TidalInfo?.artistPageLink || "",
        artistCountry: data.TidalInfo?.country || "",
        LabelDistributor: data.TidalInfo?.labelDistributor || "",
        socialsLinks: data.TidalInfo?.socialHandles || "",

        // Audiomack Info
        pitchToAudiomackTrending: data.AudiomackInfo?.pitchToTrending || false,
        audiomackSubgenre: stringToArray(data.AudiomackInfo?.subgenre),

        // SoundCloud Info
        soundCloudProfileUrl: data.SoundCloudInfo?.soundCloudProfileUrl || "",
        soundCloudContact: data.SoundCloudInfo?.soundCloudContact || "",
        isPremierProgram: data.SoundCloudInfo?.isPremierProgram || false,
        distributor: data.SoundCloudInfo?.distributor || "",
        promotionTerritories: stringToArray(data.SoundCloudInfo?.promotionTerritories),
        soundCloudSubgenre: stringToArray(data.SoundCloudInfo?.subgenre),
        soundCloudReleaseStage: data.SoundCloudInfo?.releaseStage || "",
        freshMusicFriday: stringToArray(data.SoundCloudInfo?.freshMusicFriday),
        playlistConsideration: data.SoundCloudInfo?.playlistConsideration || "",

        // Apple Info
        appleId: data.AppleInfo?.appleId || "",
        appleTrackId: data.AppleInfo?.appleTrackId || "",
      
        // YouTube Info
        youtubePromotionSurface: stringToArray(data.YoutubeInfo?.promotionSurface),
        youtubeReleaseStage: data.YoutubeInfo?.songDetails || "",
        // youtubeSongDetails: stringToArray(data.YouTubeInfo?.songDetails),
        youtubePromotionDate: data.YoutubeInfo?.promotionDate ? new Date(data.YoutubeInfo.promotionDate).toISOString().split('T')[0] : "",
        youtubeTerritories: stringToArray(data.YoutubeInfo?.territories),
      };

      setInitialFormValues(formValues);

      setSelectedDSPs({
        spotify: Boolean(data.SpotifyInfo),
        amazon: Boolean(data.AmazonInfo),
        deezer: Boolean(data.DeezerInfo),
        TIDAL: Boolean(data.TidalInfo),
        audiomack: Boolean(data.AudiomackInfo),
        soundcloud: Boolean(data.SoundCloudInfo),
        apple: Boolean(data.AppleInfo),
        youtube: Boolean(data.YoutubeInfo),
        email: false,
      });

      // Check for artist updates after loading the data
      await checkForArtistUpdates(data);

    } catch (error) {
      setFetchError("Failed to fetch submission data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchSubmission();
    }
  }, [id]);
  // Transform the form data to match the API's expected payload
  function transformSubmissionData(values: any) {
    const dsps: any = {};

    if (selectedDSPs.spotify) {
      dsps.spotify = {
        spotifyUri: values.spotifyUri,
        artistHometown: values.artistHometown,
        artistCurrentTown: values.artistCurrentTown,
        pinToPlaylist: values.pinToThisIsPlaylist,
        subgenre: values.spotifySubgenre?.join(", ") || "",
        musicCulture: values.musicCulture?.join(", ") || "",
        instruments: values.instruments?.join(", ") || "",
        songStyle: values.songStyle?.join(", ") || "",
      };
    }

    if (selectedDSPs.amazon) {
      dsps.amazon = {
        addAmazonVoiceIntro: values.addAmazonVoiceIntro,
        listenersLocation: values.listenersLocation?.join(", ") || "",
        similarArtists: values.similarArtists,
        songCategory: values.songCategory?.join(", ") || "",
        subgenre: values.amazonSubgenre?.join(", ") || "",
        asin: values.asin,
      };
    }

    if (selectedDSPs.deezer) {
      dsps.deezer = {
        priority: values.priority,
        editorialTeam: values.deezerEditorialTeam,
        subgenre: values.deezerSubgenre?.join(", ") || "",
      };
    }

    if (selectedDSPs.TIDAL) {
      dsps.tidal = {
        subgenre: values.TIDALSubgenre?.join(", ") || "",
        artistPageLink: values.TIDALpage,
        country: values.artistCountry,
        labelDistributor: values.LabelDistributor,
        socialHandles: values.socialsLinks,
      };
    }

    if (selectedDSPs.audiomack) {
      dsps.audiomack = {
        pitchToTrending: values.pitchToAudiomackTrending,
        subgenre: values.audiomackSubgenre?.join(", ") || "",
      };
    }

    if (selectedDSPs.soundcloud) {
      dsps.soundcloud = {
        soundCloudProfileUrl: values.soundCloudProfileUrl,
        soundCloudContact: values.soundCloudContact,
        subgenre: values.soundCloudSubgenre?.join(", ") || "",
        isPremierProgram: values.isPremierProgram,
        distributor: values.distributor,
        promotionTerritories: values.promotionTerritories?.join(", ") || "",
        releaseStage: values.soundCloudReleaseStage,
        freshMusicFriday: values.freshMusicFriday?.join(", ") || "",
        playlistConsideration: values.playlistConsideration,
      };
    }

    if (selectedDSPs.apple) {
    
      dsps.apple = {
        appleId: values.appleId,
        appleTrackId: values.appleTrackId,
      };
    }

    if (selectedDSPs.youtube) {
      dsps.youtube = {
        promotionSurface: values.youtubePromotionSurface?.join(", ") || "",
        // songDetails: values.youtubeReleaseStage,
        releaseStage: values.songDetails,
        promotionDate: values.youtubePromotionDate,
        territories: values.youtubeTerritories?.join(", ") || "",
      };
    }

    return {
      artistName: values.artistName,
      releaseTitle: values.releaseTitle,
      releaseDate: values.releaseDate,
      configuration: values.configuration,
      releaseUPC: values.releaseUpc || null,
      focusTrackName: values.focusTrackName,
      trackISRC: values.trackIsrc || null,
      featuredArtists: values.featuredArtists,
      featuredArtistURI: values.featuredArtistUri,
      altTitles: values.altTitles,
      shortDescription: values.shortDescription,
      longDescription: values.longDescription,
      lyrics: values.lyrics,
      songDescription: Array.isArray(values.songDescription)
        ? values.songDescription.join(", ")
        : values.songDescription,
      mainGenre: Array.isArray(values.mainGenre)
        ? values.mainGenre.join(", ")
        : values.mainGenre,
      songMood: Array.isArray(values.songMood)
        ? values.songMood.join(", ")
        : values.songMood,
      language: Array.isArray(values.language)
        ? values.language.join(", ")
        : values.language,
      linkToListen: values.linkToListen,
      promoPhotos: values.promoPhotos,
      images: Array.isArray(values.images) ? values.images : [],
      status: values.status,
      dsps: dsps,
      isEmail: selectedDSPs.email,
    };
  }

  // Function to scroll to the first error
  const scrollToFirstError = (errors: any) => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) return;

    const firstErrorKey = errorKeys[0];
    const errorField = document.querySelector(`[name="${firstErrorKey}"]`);
    
    if (errorField) {
      errorField.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      
      errorField.classList.add('ring-2', 'ring-red-500', 'bg-red-50');
      setTimeout(() => {
        errorField.classList.remove('ring-2', 'ring-red-500', 'bg-red-50');
      }, 3000);
    }
  };

  // Function to show error toast
  const showErrorToast = (errors: any) => {
    const errorCount = Object.keys(errors).length;
    const errorFields = Object.keys(errors).slice(0, 3).join(', ');
    const remainingCount = errorCount > 3 ? errorCount - 3 : 0;
    
    let errorMessage = `Please fix ${errorCount} validation error${errorCount > 1 ? 's' : ''}.`;
    if (errorCount <= 3) {
      errorMessage += ` Fields: ${errorFields}`;
    } else {
      errorMessage += ` Fields: ${errorFields}${remainingCount > 0 ? ` and ${remainingCount} more` : ''}`;
    }

    Swal.fire({
      title: 'Validation Errors',
      text: errorMessage,
      icon: 'error',
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 4000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.addEventListener('mouseenter', Swal.stopTimer)
        toast.addEventListener('mouseleave', Swal.resumeTimer)
      }
    });
  };

  const handleSubmit = async (values: any, { setSubmitting }: FormikHelpers<any>) => {
    setIsSubmitting(true);

    try {
      const submissionPayload = {
        ...values,
        status: "submitted",
      };

      const response = await pitchesApi.update(
        Number(id),
        transformSubmissionData(submissionPayload)
      );

      if (!response?.data.success) {
        throw new Error(response.statusText || "Update failed");
      }

      navigate("/dashboard");
    } catch (error: any) {
      console.error("Update error:", error);
      
      const errorMessages =
        error.response?.data?.details && Array.isArray(error.response.data.details)
          ? error.response.data.details
              .map((detail: { field: string; message: string }) =>
                `<b>${detail.field}</b>: ${detail.message}`
              )
              .join("<br>")
          : error.response?.data?.message || "An unexpected error occurred";

      Swal.fire({
        title: "Error!",
        html: errorMessages,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async (values: any) => {
    setIsDrafting(true);

    try {
      const submissionPayload = {
        ...values,
        status: "draft",
      };

      const response = await pitchesApi.updateDraft(
        Number(id),
        transformSubmissionData(submissionPayload)
      );

      if (!response?.data.success) {
        throw new Error(response.statusText || "Draft save failed");
      }

      Swal.fire({
        title: "Success!",
        text: "Draft saved successfully",
        icon: "success",
        confirmButtonText: "OK",
      });
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Draft save error:", error);

      Swal.fire({
        title: "Error!",
        text: error.response?.data?.message || "Failed to save draft",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsDrafting(false);
    }
  };

  const handleRetry = () => {
    setFetchError("");
    setLoading(true);
    fetchSubmission();
  };

  // Create dynamic validation schema based on selected DSPs
  const validationSchema = React.useMemo(() => {
    return createPitchValidationSchema(selectedDSPs);
  }, [selectedDSPs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-32 h-32 border-b-2 border-blue-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="p-6 border border-red-200 rounded-md bg-red-50">
        <p className="text-red-600">{fetchError}</p>
        <div className="flex gap-4 mt-4">
          <button
            onClick={handleRetry}
            className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Retry
          </button>
          <button
            onClick={() => navigate("/dashboard")}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="p-6 rounded-lg shadow-lg bg-gray-50">
      <button
        onClick={() => navigate("/dashboard")}
        className="flex items-center mb-4 text-gray-600 hover:text-gray-800"
      >
        <ArrowLeftIcon className="w-5 h-5 mr-2" /> Back
      </button>

      {/* Artist Update Prompt */}
      {showArtistUpdatePrompt && artistUpdates && (
        <div className="p-4 mb-6 border border-blue-200 rounded-lg bg-blue-50">
          <div className="flex items-start space-x-3">
            <svg
              className="w-5 h-5 text-blue-400 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-blue-800">
                Artist Information Updated
              </h3>
              <p className="mt-1 text-sm text-blue-700">
                The artist "{currentPitchData?.artistName}" information has been updated in your roster. 
                Would you like to update this draft with the latest artist data?
              </p>
              {artistUpdates.changes && (
                <div className="mt-2">
                  <p className="text-xs font-medium text-blue-600">Updated fields:</p>
                  <ul className="text-xs text-blue-600 list-disc list-inside">
                    {artistUpdates.changes.map((change: string, index: number) => (
                      <li key={index}>{change}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex gap-3 mt-4">
                <button
                  onClick={applyArtistUpdates}
                  disabled={loading}
                  className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? "Updating..." : "Update Draft"}
                </button>
                <button
                  onClick={() => setShowArtistUpdatePrompt(false)}
                  className="px-3 py-1 text-sm font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200"
                >
                  Keep Current Data
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove the Error Summary Banner section */}

      <Formik
        initialValues={initialFormValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ values, isValid, errors, touched, setSubmitting }) => (
          <Form>
            <div className="p-5 mb-8 rounded-lg bg-gray-50">
              <ReleaseInfo />
            </div>

            <div className="p-5 mb-8 rounded-lg bg-gray-50">
              <PlatformSelection
                selectedDSPs={selectedDSPs}
                setSelectedDSPs={setSelectedDSPs}
              />
            </div>

            <div className="p-5 mb-8 rounded-lg bg-gray-50">
              <GeneralReleaseInfo />
            </div>

            <div className="p-5 mb-8 rounded-lg bg-gray-50">
              <DspSpecificInfo selectedDSPs={selectedDSPs} />
            </div>

            <div className="flex justify-end gap-4 mt-8">
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="px-6 py-2 text-white bg-gray-400 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isSubmitting || isDrafting}
              >
                Cancel
              </button>

              {values.status === "draft" && (
                <button
                  type="button"
                  onClick={() => handleSaveDraft(values)}
                  disabled={isSubmitting || isDrafting}
                  className="px-6 py-2 text-white rounded-md bg-cyan-500 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDrafting ? (
                    <>
                      <span className="inline-block w-4 h-4 mr-2 border-2 border-white rounded-full animate-spin border-t-transparent"></span>
                      Saving Draft...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              )}

              <button
                type="submit"
                disabled={isSubmitting || isDrafting}
                onClick={() => {
                  if (Object.keys(errors).length > 0) {
                    showErrorToast(errors);
                    scrollToFirstError(errors);
                  }
                }}
                className="px-6 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <span className="inline-block w-4 h-4 mr-2 border-2 border-white rounded-full animate-spin border-t-transparent"></span>
                    submitting...
                  </>
                ) : (
                  "Submit Draft"
                )}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default EditFormPage;